import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { toast } from 'react-hot-toast';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Log browser info for debugging Safari issues
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
        console.log('Auth callback - Browser:', isSafari ? 'Safari' : 'Other', userAgent);
        
        // Get the auth code and other parameters from the URL
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        const type = searchParams.get('type');
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        
        console.log('Auth callback params:', { code: !!code, error, type, accessToken: !!accessToken, refreshToken: !!refreshToken });

        // Handle error cases
        if (error) {
          console.error('Auth callback error:', error, errorDescription);
          setError(errorDescription || error);
          toast.error(`Authentication failed: ${errorDescription || error}`);
          setIsProcessing(false);
          
          // Redirect to login after a delay
          setTimeout(() => {
            navigate('/login');
          }, 3000);
          return;
        }

        // Force password reset flow when type is 'recovery' or we have 'passwordReset' in the URL - this is critical for Safari compatibility
        if (type === 'recovery' || searchParams.get('passwordReset') === 'true' || window.location.href.includes('passwordReset') || window.location.hash.includes('type=recovery')) {
          // Password reset callback - ALWAYS redirect to reset-password-confirm
          console.log('Processing password reset callback');
          
          // For all browsers, try multiple approaches to handle the session
          if (code) {
            try {
              // Primary method: Exchange code for session
              const { data, error: sessionError } = await supabase.auth.exchangeCodeForSession(code);
              
              if (sessionError) {
                console.error('Primary session exchange failed:', sessionError);
                
                // Fallback: Try to set session directly if we have tokens
                if (accessToken && refreshToken) {
                  console.log('Trying fallback with direct token setting');
                  const { data: fallbackData, error: fallbackError } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken
                  });
                  
                  if (fallbackError) {
                    console.error('Fallback also failed:', fallbackError);
                    setError('Failed to process password reset link. Please try again or contact support.');
                    toast.error('Failed to process password reset link. Please try again.');
                    setTimeout(() => navigate('/login'), 3000);
                    return;
                  }
                  
                  if (fallbackData.session) {
                    console.log('Fallback successful');
                    toast.success('Password reset link verified! Please set your new password.');
                    // IMPORTANT: ALWAYS redirect to reset-password-confirm for password reset
                    navigate('/reset-password-confirm');
                    return;
                  }
                } else {
                  setError('Failed to process password reset link');
                  toast.error('Failed to process password reset link');
                  setTimeout(() => navigate('/login'), 3000);
                  return;
                }
              }
              
              if (data.session) {
                console.log('Primary session exchange successful');
                toast.success('Password reset link verified! Please set your new password.');
                // IMPORTANT: ALWAYS redirect to reset-password-confirm for password reset
                navigate('/reset-password-confirm');
                return;
              }
            } catch (err) {
              console.error('Unexpected error in password reset:', err);
              setError('An unexpected error occurred processing the password reset link');
              toast.error('An unexpected error occurred. Please try again.');
              setTimeout(() => navigate('/login'), 3000);
              return;
            }
          }
        } else if (type === 'magiclink' || searchParams.get('magiclink') === 'true' || window.location.href.includes('magiclink')) {
          // Magic link login flow
          console.log('Processing magic link login flow');
          
          // Handle direct token login if available
          if (accessToken && refreshToken) {
            try {
              const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
              });
              
              if (sessionError) {
                console.error('Magic link session failed:', sessionError);
                setError('Failed to process magic link');
                toast.error('Failed to authenticate with magic link');
                setTimeout(() => navigate('/login'), 3000);
                return;
              }
              
              if (sessionData.session) {
                console.log('Magic link login successful');
                toast.success('Successfully logged in!');
                try {
                  const redirect = localStorage.getItem('post_reset_redirect');
                  if (redirect && redirect.trim().length > 0) {
                    localStorage.removeItem('post_reset_redirect');
                    navigate(redirect);
                    return;
                  }
                } catch {}
                navigate('/');
                return;
              }
            } catch (err) {
              console.error('Error processing magic link:', err);
              setError('Failed to process magic link');
              toast.error('Failed to authenticate with magic link');
              setTimeout(() => navigate('/login'), 3000);
              return;
            }
          }
          
          // Fall back to code exchange if tokens not available
          if (code) {
            try {
              const { data, error: codeError } = await supabase.auth.exchangeCodeForSession(code);
              
              if (codeError) {
                console.error('Magic link code exchange failed:', codeError);
                setError('Failed to authenticate with magic link');
                toast.error('Authentication failed. Please try again.');
                setTimeout(() => navigate('/login'), 3000);
                return;
              }
              
              if (data.session) {
                console.log('Magic link code exchange successful');
                toast.success('Successfully logged in!');
                try {
                  const redirect = localStorage.getItem('post_reset_redirect');
                  if (redirect && redirect.trim().length > 0) {
                    localStorage.removeItem('post_reset_redirect');
                    navigate(redirect);
                    return;
                  }
                } catch {}
                navigate('/');
                return;
              }
            } catch (err) {
              console.error('Unexpected error in magic link flow:', err);
              setError('An unexpected error occurred');
              toast.error('An unexpected error occurred. Please try again.');
              setTimeout(() => navigate('/login'), 3000);
              return;
            }
          }
        } else {
          // Email verification or signup callback
          console.log('Processing email verification callback');
          
          if (code) {
            try {
              // Primary method: Exchange code for session
              const { data, error: sessionError } = await supabase.auth.exchangeCodeForSession(code);
              
              if (sessionError) {
                console.error('Primary email verification failed:', sessionError);
                
                // Safari fallback: Try to set session directly if we have tokens
                if (accessToken && refreshToken) {
                  console.log('Trying Safari fallback for email verification');
                  const { data: fallbackData, error: fallbackError } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken
                  });
                  
                  if (fallbackError) {
                    console.error('Safari email verification fallback failed:', fallbackError);
                    setError('Failed to verify email address. Please try again or contact support.');
                    toast.error('Failed to verify email address. Please try again.');
                    setTimeout(() => navigate('/login'), 3000);
                    return;
                  }
                  
                  if (fallbackData.session && fallbackData.user) {
                    console.log('Safari email verification fallback successful:', fallbackData.user.email);
                    toast.success('Email verified successfully! Welcome to Explanatum.');
                    navigate('/');
                    return;
                  }
                } else {
                  setError('Failed to verify email address');
                  toast.error('Failed to verify email address');
                  setTimeout(() => navigate('/login'), 3000);
                  return;
                }
              }
              
              if (data.session && data.user) {
                console.log('Primary email verification successful:', data.user.email);
                toast.success('Email verified successfully! Welcome to Explanatum.');
                navigate('/');
                return;
              }
            } catch (err) {
              console.error('Unexpected error in email verification:', err);
              setError('An unexpected error occurred during email verification');
              toast.error('An unexpected error occurred. Please try again.');
              setTimeout(() => navigate('/login'), 3000);
              return;
            }
          }
        }

        // If we get here without a code, try to get the current session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error getting session:', sessionError);
          setError('Authentication session error');
          toast.error('Authentication session error');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        if (sessionData.session) {
          console.log('Session found, user authenticated');
          toast.success('Successfully authenticated!');
          navigate('/');
        } else {
          console.log('No session found, redirecting to login');
          setError('No authentication session found');
          toast.error('Authentication link may have expired');
          setTimeout(() => navigate('/login'), 3000);
        }

      } catch (err) {
        console.error('Unexpected error in auth callback:', err);
        setError('An unexpected error occurred');
        toast.error('An unexpected error occurred during authentication');
        setTimeout(() => navigate('/login'), 3000);
      } finally {
        setIsProcessing(false);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-blue-600">
              <svg className="animate-spin h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Verifying your account...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we process your authentication.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Authentication Error
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {error}
            </p>
            <p className="mt-4 text-xs text-gray-500">
              You will be redirected to the login page shortly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // This shouldn't be reached, but just in case
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Authentication Complete
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Redirecting you now...
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
